import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  CheckCheck, Ban, Loader2, XCircle, MailCheck, Scissors, FileText, Wrench,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_META = {
  remove:  { icon: Scissors, label: 'Remove this component from scope' },
  clarify: { icon: FileText, label: 'Clarification from the team' },
  other:   { icon: Wrench,   label: 'Scope adjustment' },
};

export default function ChangeRequestDecisionPage() {
  const { crId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cr, setCr] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/change-requests/public/${crId}`);
      setCr(res.data);
    } catch (e) {
      setError(e?.response?.status === 404 ? 'This link is invalid or has expired.' : 'Could not load this change request.');
    } finally { setLoading(false); }
  }, [crId]);

  useEffect(() => { load(); }, [load]);

  const submitDecision = async (decision) => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/change-requests/public/${crId}/decision`,
        { decision, note: note.trim() });
      await load();
      toast.success(decision === 'accepted' ? 'Thanks — the team will proceed.' : 'Thanks — we\u2019ll follow up.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-[#0F2040]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full" data-testid="cr-error-card">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-10 h-10 mx-auto text-rose-500 mb-3" />
            <p className="text-slate-700 text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const decided = cr.decision === 'accepted' || cr.decision === 'declined';
  const ActionIcon = (ACTION_META[cr.proposed_action] || ACTION_META.other).icon;
  const actionLabel = (ACTION_META[cr.proposed_action] || ACTION_META.other).label;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="change-request-page">
      <div className="max-w-xl mx-auto">
        <div className="bg-[#0F2040] text-white rounded-t-lg px-6 py-5">
          <div className="text-[11px] text-[#4ABFCF] uppercase tracking-wide font-bold">SEERTECH SOLUTIONS</div>
          <h1 className="text-lg font-bold mt-0.5">Scope adjustment proposed</h1>
          <p className="text-[11px] text-slate-300 mt-1">
            {cr.estimate_name || 'Your implementation plan'} &middot; {cr.component_name}
          </p>
        </div>
        <Card className="rounded-t-none border-t-0">
          <CardContent className="pt-6 space-y-4">
            {cr.feedback_note && (
              <div className="bg-slate-50 border border-slate-100 rounded-md p-3">
                <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Your earlier note</div>
                <div className="text-xs italic text-slate-700">&ldquo;{cr.feedback_note}&rdquo;</div>
              </div>
            )}

            <div className="border border-slate-200 rounded-md p-3">
              <div className="flex items-center gap-2 text-[11px] text-slate-500 uppercase tracking-wide font-bold">
                <ActionIcon className="w-3.5 h-3.5 text-[#0F2040]" />
                {actionLabel}
              </div>
              <div className="text-sm text-[#0F2040] mt-2 whitespace-pre-wrap">{cr.proposed_note}</div>
              {cr.consultant_name && (
                <div className="text-[10px] text-slate-400 mt-2">proposed by {cr.consultant_name}</div>
              )}
            </div>

            {decided ? (
              <div className="bg-slate-50 border border-slate-100 rounded-md p-4 text-center">
                {cr.decision === 'accepted' ? (
                  <>
                    <CheckCheck className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                    <div className="text-sm font-semibold text-[#0F2040]">You&rsquo;ve accepted this change.</div>
                  </>
                ) : (
                  <>
                    <Ban className="w-8 h-8 mx-auto text-rose-500 mb-2" />
                    <div className="text-sm font-semibold text-[#0F2040]">You declined this change.</div>
                  </>
                )}
                {cr.decision_note && (
                  <div className="text-[11px] text-slate-600 italic mt-2">&ldquo;{cr.decision_note}&rdquo;</div>
                )}
                {cr.resolved_at && (
                  <div className="text-[10px] text-slate-400 mt-2">{new Date(cr.resolved_at).toLocaleString()}</div>
                )}
                <div className="mt-3">
                  <MailCheck className="w-4 h-4 inline text-slate-400 mr-1" />
                  <span className="text-[11px] text-slate-500">The team has been notified.</span>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs font-medium text-[#0F2040]">Optional note</label>
                  <Textarea value={note} onChange={e => setNote(e.target.value)} rows={3} maxLength={1000}
                    className="text-xs mt-1"
                    placeholder="Add any context for the team..."
                    data-testid="cr-decision-note" />
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => submitDecision('declined')} disabled={submitting}
                    variant="outline" className="flex-1 border-rose-200 text-rose-700 hover:bg-rose-50"
                    data-testid="cr-decline-btn">
                    <Ban className="w-3.5 h-3.5 mr-1.5" /> Decline
                  </Button>
                  <Button onClick={() => submitDecision('accepted')} disabled={submitting}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="cr-accept-btn">
                    {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                : <CheckCheck className="w-3.5 h-3.5 mr-1.5" />}
                    Accept
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        <p className="text-center text-[10px] text-slate-400 mt-4">
          Secure link &middot; only the person who received the email should respond.
        </p>
      </div>
    </div>
  );
}
